import type { AvailabilityRule } from '../types';

export interface Weekday {
  code: string;
  short: string;
  label: string;
}

/** RFC-5545 BYDAY codes in canonical Mon..Sun order. */
export const WEEKDAYS: Weekday[] = [
  { code: 'MO', short: 'Mon', label: 'Monday' },
  { code: 'TU', short: 'Tue', label: 'Tuesday' },
  { code: 'WE', short: 'Wed', label: 'Wednesday' },
  { code: 'TH', short: 'Thu', label: 'Thursday' },
  { code: 'FR', short: 'Fri', label: 'Friday' },
  { code: 'SA', short: 'Sat', label: 'Saturday' },
  { code: 'SU', short: 'Sun', label: 'Sunday' },
];

const DAY_SHORT: Record<string, string> = Object.fromEntries(
  WEEKDAYS.map(d => [d.code, d.short]),
);

/** Build a weekly RRULE from selected BYDAY codes, or null for a one-off. */
export function buildWeeklyRRule(days: string[]): string | null {
  if (days.length === 0) return null;
  const ordered = WEEKDAYS.filter(d => days.includes(d.code)).map(d => d.code);
  return `FREQ=WEEKLY;BYDAY=${ordered.join(',')}`;
}

/** Parse BYDAY codes out of an rrule (normalized or not). */
export function parseByDay(rrule?: string | null): string[] {
  if (!rrule) return [];
  const match = /BYDAY=([^;]+)/i.exec(rrule);
  return match ? match[1].split(',').map(s => s.trim().toUpperCase()) : [];
}

export function formatTimeOfDay(iso: string, timezone?: string): string {
  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone || undefined,
  }).format(new Date(iso));
}

export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours && mins) return `${hours}h ${mins}m`;
  if (hours) return `${hours}h`;
  return `${mins}m`;
}

/** Human summary of a rule's recurrence + time window for the list. */
export function summarizeSchedule(rule: AvailabilityRule): string {
  const days = parseByDay(rule.rrule);
  const time = formatTimeOfDay(rule.start_time, rule.timezone);
  const duration = formatDuration(rule.duration_minutes);
  if (days.length > 0) {
    const label = days.map(d => DAY_SHORT[d] ?? d).join(', ');
    return `Weekly · ${label} · ${time} (${duration})`;
  }
  const date = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    timeZone: rule.timezone || undefined,
  }).format(new Date(rule.start_time));
  return `One-time · ${date} · ${time} (${duration})`;
}

/** The browser's IANA timezone — the zone new windows are authored in. */
export function browserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
}

/**
 * Build a tz-aware start_time instant for a window authored at `timeHHmm`
 * browser-local. The Date is created in browser-local time, so its UTC instant
 * (toISOString) converts back to that wall-clock when the backend expands in
 * the browser timezone (start_time.astimezone(tz)). For recurring rules only
 * the time-of-day + BYDAY matter; the date is just an anchor (today).
 */
export function buildStartTimeIso(timeHHmm: string): string {
  const [hours, minutes] = timeHHmm.split(':').map(Number);
  const now = new Date();
  const anchor = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    hours || 0,
    minutes || 0,
    0,
    0,
  );
  return anchor.toISOString();
}

/** Minutes between two HH:mm times; negative/zero if end is not after start. */
export function durationFromTimes(startHHmm: string, endHHmm: string): number {
  const [startH, startM] = startHHmm.split(':').map(Number);
  const [endH, endM] = endHHmm.split(':').map(Number);
  return endH * 60 + endM - (startH * 60 + startM);
}

/** A rule's start as 24h "HH:mm" wall-clock in its authored timezone — the
 * shape a `<input type="time">` expects. */
export function timeOfDayHHmm(iso: string, timezone?: string): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: timezone || undefined,
  }).formatToParts(new Date(iso));
  const hour = parts.find(p => p.type === 'hour')?.value ?? '00';
  const minute = parts.find(p => p.type === 'minute')?.value ?? '00';
  return `${hour}:${minute}`;
}

/** Add minutes to an "HH:mm" time, clamped within a single day (availability
 * windows don't cross midnight). */
export function addMinutesHHmm(hhmm: string, minutes: number): string {
  const [hours, mins] = hhmm.split(':').map(Number);
  const total = Math.min(hours * 60 + mins + minutes, 23 * 60 + 59);
  const hh = String(Math.floor(total / 60)).padStart(2, '0');
  const mm = String(total % 60).padStart(2, '0');
  return `${hh}:${mm}`;
}

export interface ScheduleDraft {
  days: string[];
  startTime: string;
  endTime: string;
}

/** Seed the add/edit form fields from an existing rule (edit mode). */
export function ruleToDraft(rule: {
  rrule?: string | null;
  start_time: string;
  duration_minutes: number;
  timezone: string;
}): ScheduleDraft {
  const startTime = timeOfDayHHmm(rule.start_time, rule.timezone);
  return {
    days: parseByDay(rule.rrule),
    startTime,
    endTime: addMinutesHHmm(startTime, rule.duration_minutes),
  };
}
